import {
  compactEth, formatEth, formatTokens, formatPpm, formatAge, parseEthInput, quoteBuy, quoteSell,
  spotPriceWei, GRADUATION_THRESHOLD, SNIPE_WINDOW, THRESHOLD, type Candle, type CurveSnapshot,
  type TokenSummary, type Trade,
} from '@colosseum/shared';
import type {Address} from 'viem';
import {formatUnits, parseUnits, erc20Abi} from 'viem';
import {api} from '../api.js';
import {publicClient, quoteBuyOnChain, quoteSellOnChain} from '../chain.js';
import {addressChip, tokenArt} from '../ui/components.js';
import {$, html, raw, setHtml, setText} from '../ui/dom.js';
import {closeModal, modalIsOpen, notify, txProgress} from '../ui/feedback.js';
import {buy, collect, describeError, sell} from '../tx.js';
import {wallet, onRightChain} from '../wallet.js';
import {candleChart} from '../ui/chart.js';

/** One token: its chart, its place in this period's draw, and the trade panel. */

interface Loaded {
  token: TokenSummary;
  curve: CurveSnapshot;
  trades: Trade[];
  candles: Candle[];
  balance: bigint;
}

let current: Loaded | null = null;
let side: 'buy' | 'sell' = 'buy';
let slippageBps = 100n; // 1%
let refreshTimer: ReturnType<typeof setInterval> | undefined;

export function tokenView(address: string): string {
  void address;
  return html`<section class="subpage token-page" id="token-page">
    <div class="loading">Loading…</div>
  </section>`;
}

export async function mountToken(address: string): Promise<() => void> {
  await load(address);
  refreshTimer = setInterval(() => void load(address, true), 8000);
  return () => {
    clearInterval(refreshTimer);
    current = null;
  };
}

async function load(address: string, quiet = false): Promise<void> {
  // A background refresh must never disturb someone mid-trade: rebuilding the
  // panel would drop what they had typed and take the caret with it.
  if (quiet && document.activeElement?.id === 'trade-amount') return;
  if (quiet && modalIsOpen()) return;
  try {
    const to = Math.floor(Date.now() / 1000);
    const detail = await api.token(address);
    // A token minutes old has every trade inside one five-minute bucket, which
    // is a chart of one point. Scale the bucket to how long it has existed.
    const age = to - detail.token.createdAt;
    const interval = age < 2 * 3600 ? 60 : age < 12 * 3600 ? 300 : 900;
    const [trades, candles] = await Promise.all([
      api.trades(address, 40).catch(() => [] as Trade[]),
      api.candles(address, interval, to - 86_400, to).catch(() => [] as Candle[]),
    ]);
    let balance = 0n;
    if (wallet.address) {
      balance = await publicClient()
        .readContract({address: address as Address, abi: erc20Abi, functionName: 'balanceOf', args: [wallet.address]})
        .catch(() => 0n);
    }
    current = {token: detail.token, curve: detail.curve, trades, candles, balance};
    render();
  } catch (e) {
    if (quiet) return;
    setHtml(
      $('#token-page'),
      html`<div class="empty">Could not load this token.<br><small>${(e as Error).message}</small>
        <br><a class="secondary" href="#/">Back to the arena</a></div>`,
    );
  }
}

function render(): void {
  if (!current) return;
  // Carry the trade input across a re-render, so a refresh that lands while
  // someone is deciding does not clear the amount they entered.
  const previous = $<HTMLInputElement>('#trade-amount');
  const carried = previous?.value ?? '';
  const {token: t, curve: c, trades, candles} = current;
  const price = BigInt(t.priceWei);
  const graduated = t.graduated;

  setHtml(
    $('#token-page'),
    html`<div class="token-head">
      <div class="detail-ident">
        ${raw(tokenArt(t))}
        <div>
          <h1>${t.name}</h1>
          <p>$${t.symbol} · ${raw(addressChip(t.address, 'token'))}
            ${raw(
            graduated
              ? '<span class="tag tag-inline tag-pool">LOCKED POOL</span>'
              : '<span class="tag tag-inline tag-curve">ON THE CURVE</span>',
          )}
          </p>
          ${raw(t.description ? html`<p class="token-desc">${t.description}</p>` : '')}
          ${raw(socialLinks(t))}
        </div>
      </div>
      <div class="detail-metrics">
        <div><small>PRICE</small><b>${formatEth(price, {places: 12})}<i>ETH</i></b></div>
        <div><small>MARKET VALUE</small><b>${compactEth(BigInt(t.marketCapWei))}<i>ETH</i></b></div>
        <div><small>VOLUME 24H</small><b>${compactEth(BigInt(t.volume24hWei))}<i>ETH</i></b></div>
        <div><small>HOLDERS</small><b>${t.holders}</b></div>
      </div>
    </div>

    <div class="token-body">
      <div class="token-main">
        <div class="panel chart-panel">
          ${raw(candleChart(candles, price, trades))}
        </div>

        ${raw(drawStanding(t))}
        ${raw(graduated ? poolPanel(t) : curvePanel(c))}

        <div class="panel">
          <h3 class="panel-title">Recent trades</h3>
          ${raw(tradeTable(trades, t.symbol))}
        </div>
      </div>

      <aside class="token-side">
        ${raw(tradePanel())}
        ${raw(supplyPanel(t))}
      </aside>
    </div>`,
  );
  const next = $<HTMLInputElement>('#trade-amount');
  if (next && carried) next.value = carried;
  bindTrade();
}

/** Where this token stands in the open period's draw, and what that is worth. */
/**
 * Links the creator set at launch. They are whatever string was written into
 * the token contract, so anything that is not plainly an http(s) URL is shown
 * as text rather than turned into a link.
 */
function socialLinks(t: TokenSummary): string {
  const entries: [string, string][] = [
    ['X', t.socials.twitter], ['Telegram', t.socials.telegram], ['Discord', t.socials.discord],
    ['Website', t.socials.website], ['Farcaster', t.socials.farcaster],
  ];
  const shown = entries.filter(([, v]) => v && v.trim());
  if (!shown.length) return '';
  return html`<div class="socials-row">${shown.map(([label, url]) =>
    raw(
      /^https?:\/\//i.test(url)
        ? html`<a class="chip" href="${url}" target="_blank" rel="noopener nofollow ugc">${label}</a>`
        : html`<span class="chip" title="${url}">${label}</span>`,
    ),
  )}</div>`;
}

function drawStanding(t: TokenSummary): string {
  const fees = BigInt(t.feesPeriodWei);
  const short = THRESHOLD - fees;
  return html`<div class="panel standing">
    <h3 class="panel-title">This period's draw</h3>
    ${raw(
      t.qualified
        ? html`<div class="standing-in">
            <div class="standing-odds">${formatPpm(t.oddsPpm)}</div>
            <div>
              <b>In the draw.</b>
              <span>Its trades have paid ${formatEth(fees, {places: 4})} ETH of fees this period. A token's chance is
              its share of the fees paid, so this is both at once.</span>
            </div>
          </div>`
        : html`<div class="standing-out">
            <b>Not in this draw yet.</b>
            <span>Its trades have paid ${formatEth(fees, {places: 4})} ETH of the 0.05 ETH a token needs to enter,
            ${formatEth(short > 0n ? short : 0n, {places: 4})} ETH short. The threshold keeps tokens with negligible
            trading out of the list; it does not change any token's chance above it.</span>
          </div>`,
    )}
  </div>`;
}

function curvePanel(c: CurveSnapshot): string {
  const raised = BigInt(c.trackedQuote);
  const pct = Number((raised * 1000n) / GRADUATION_THRESHOLD) / 10;
  return html`<div class="panel curve-panel">
    <h3 class="panel-title">The curve</h3>
    <div class="curve-progress"><i style="width:${Math.min(100, pct).toFixed(1)}%"></i></div>
    <div class="curve-legend">
      <span><b>${formatEth(raised, {places: 3})}</b> of 4 ETH raised</span>
      <span><b>${formatTokens(BigInt(c.trackedTokens))}</b> tokens left on the curve</span>
    </div>
    <p class="muted small">At 4 ETH the curve closes. Its 4 ETH and 160,000,000 tokens become a Uniswap v4 position
      that is locked forever — no withdrawal, no arbitrary call — and 40,000,000 tokens are burned. The creator
      supplies no liquidity at either stage.</p>
  </div>`;
}

function poolPanel(t: TokenSummary): string {
  return html`<div class="panel curve-panel">
    <h3 class="panel-title">The locked pool</h3>
    <p class="muted small">This token graduated. Its liquidity is a full-range Uniswap v4 position held by the locker,
      which has no withdrawal function and no way to call arbitrary code. The pool's 1.3% fee accrues to that
      position; anyone may collect it, and when they do it is split like every other fee and credited to the token
      for the period it is collected in.</p>
    <div class="pool-actions">
      <button class="secondary" data-action="collect" data-token="${t.address}">Collect this pool's fees</button>
      <span class="muted small">Anyone may call this. It puts the pool's accrued fees into the open period.</span>
    </div>
  </div>`;
}

function supplyPanel(t: TokenSummary): string {
  const supply = BigInt(t.totalSupply);
  const burned = BigInt(t.burnedTotal);
  const original = supply + burned;
  const pct = original === 0n ? 0 : Number((burned * 10_000n) / original) / 100;
  return html`<div class="panel supply-panel">
    <h3 class="panel-title">Supply</h3>
    <dl>
      <div><dt>Now</dt><dd>${formatTokens(supply)}</dd></div>
      <div><dt>Burned</dt><dd>${formatTokens(burned)} <small>(${pct.toFixed(2)}%)</small></dd></div>
      <div><dt>Created</dt><dd>1,000,000,000</dd></div>
    </dl>
    <p class="muted small">Fixed supply, no mint function, no owner. Everything the arena buys is destroyed, as are
      the 40,000,000 that do not enter the pool at graduation.</p>
    <div class="meta-row"><span>Creator</span>${raw(addressChip(t.creator))}</div>
    <div class="meta-row"><span>Curve</span>${raw(addressChip(t.curve))}</div>
    <div class="meta-row"><span>Launched</span><span>${formatAge(t.createdAt)}</span></div>
  </div>`;
}

function tradeTable(trades: Trade[], symbol: string): string {
  if (!trades.length) return html`<p class="muted small">No trades yet.</p>`;
  return html`<table class="trades">
    <thead><tr><th>WHEN</th><th>SIDE</th><th>ETH</th><th>${symbol}</th><th>WHERE</th></tr></thead>
    <tbody>${trades.map(
      (t) => raw(html`<tr class="${t.side}">
        <td class="muted">${formatAge(t.time)}</td>
        <td class="${t.side}">${t.side.toUpperCase()}</td>
        <td>${formatEth(BigInt(t.ethWei), {places: 4})}</td>
        <td>${formatTokens(BigInt(t.tokens))}</td>
        <td class="muted">${t.venue === 'pool' ? 'pool' : 'curve'}</td>
      </tr>`),
    )}</tbody>
  </table>`;
}

// ------------------------------------------------------------------ trade panel

function tradePanel(): string {
  if (!current) return '';
  const {token: t, curve: c, balance} = current;
  if (t.graduated) {
    return html`<div class="panel trade-panel">
      <h3 class="panel-title">Trade</h3>
      <p class="muted small">This token has left its curve and trades in its Uniswap v4 pool. Pool trading is not
        wired into this build yet — the curve is. Its pool is ETH / ${t.symbol} at a 1.3% fee, tick spacing 200,
        no hook.</p>
      <div class="meta-row"><span>Token</span>${raw(addressChip(t.address, 'token'))}</div>
    </div>`;
  }
  const inWindow = c.snipeWindowEndsIn > 0;
  return html`<div class="panel trade-panel">
    <div class="trade-tabs" role="group" aria-label="Trade direction">
      <button type="button" class="${side === 'buy' ? 'active' : ''}" data-action="side" data-side="buy">Buy</button>
      <button type="button" class="sell ${side === 'sell' ? 'active' : ''}" data-action="side" data-side="sell">Sell</button>
    </div>

    ${raw(
      inWindow
        ? html`<div class="warn snipe">
            <b>Launch window: ${c.snipeWindowEndsIn}s left.</b>
            A buy by anyone but the creator pays a 99% tax for the first ${SNIPE_WINDOW} seconds after a launch.
            It is what keeps bots off the opening price. Wait for the countdown.
          </div>`
        : '',
    )}

    <div class="field">
      <label for="trade-amount">${side === 'buy' ? 'You pay · ETH' : `You sell · ${t.symbol}`}</label>
      <input id="trade-amount" type="text" inputmode="decimal" autocomplete="off" placeholder="0.0">
      ${raw(
        side === 'sell'
          ? html`<small class="balance">Balance ${formatTokens(balance)} ${t.symbol}
              <button class="link" data-action="max">max</button></small>`
          : '',
      )}
    </div>

    <div class="presets">${raw(
      (side === 'buy' ? ['0.01', '0.05', '0.25', '1'] : ['25%', '50%', '100%'])
        .map((v) => `<button type="button" data-action="preset" data-value="${v}">${v}</button>`)
        .join(''),
    )}</div>

    <div class="quote" id="quote"><span>Enter an amount</span></div>
    <div id="quote-detail"></div>

    <button class="primary full" data-action="trade" id="trade-submit" disabled>
      ${side === 'buy' ? 'Buy' : 'Sell'} ${t.symbol}
    </button>

    <div class="slippage">
      <span>Slippage</span>
      ${raw(
        ['50', '100', '300'].map(
          (b) => `<button data-action="slip" data-bps="${b}" class="${String(slippageBps) === b ? 'active' : ''}">${Number(b) / 100}%</button>`,
        ).join(''),
      )}
    </div>
    <p class="form-note">Every trade pays 1.3% of its ETH leg: 0.5% to the creator, 0.5% into the pool this token is
      then in the draw for, 0.3% to the protocol.</p>
  </div>`;
}

function bindTrade(): void {
  const input = $<HTMLInputElement>('#trade-amount');
  if (!input) return;
  input.oninput = () => void updateQuote();
  void updateQuote();
}

/** Local mirror first so the number moves as you type, then the contract confirms it. */
async function updateQuote(): Promise<void> {
  if (!current || current.token.graduated) return;
  const input = $<HTMLInputElement>('#trade-amount');
  const quoteEl = $('#quote');
  const detailEl = $('#quote-detail');
  const submit = $<HTMLButtonElement>('#trade-submit');
  if (!input || !quoteEl || !submit) return;

  const {token: t, curve: c, balance} = current;
  const state = {
    trackedQuote: BigInt(c.trackedQuote),
    trackedTokens: BigInt(c.trackedTokens),
    reservedTokens: BigInt(c.reservedTokens),
    graduated: c.graduated,
  };

  if (side === 'buy') {
    const wei = parseEthInput(input.value);
    if (wei === null || wei === 0n) {
      setHtml(quoteEl, html`<span>Enter an amount</span>`);
      setHtml(detailEl, '');
      submit.disabled = true;
      return;
    }
    const q = quoteBuy(state, wei, c.snipeWindowEndsIn > 0 && wallet.address !== t.creator ? 9900n : 0n);
    if (q.tokensOut === 0n) {
      setHtml(quoteEl, html`<span>The curve cannot fill this.</span>`);
      submit.disabled = true;
      return;
    }
    setHtml(quoteEl, html`You receive<strong>${formatTokens(q.tokensOut)} ${t.symbol}</strong>`);
    const bits = [
      html`<div class="qd"><span>Fee (1.3%)</span><span>${formatEth(q.fee, {places: 6})} ETH</span></div>`,
      html`<div class="qd"><span>Into this period's pool</span><span>${formatEth((q.fee * 50n) / 130n, {places: 6})} ETH</span></div>`,
    ];
    if (q.tax > 0n) {
      bits.push(html`<div class="qd warn-row"><span>Launch-window tax (99%)</span><span>${formatEth(q.tax, {places: 6})} ETH</span></div>`);
    }
    if (q.refund > 0n) {
      bits.push(html`<div class="qd"><span>Refunded (curve filled up)</span><span>${formatEth(q.refund, {places: 6})} ETH</span></div>`);
    }
    if (q.graduates) {
      bits.push(html`<div class="qd good-row"><span>This buy graduates the token</span><span>4 ETH reached</span></div>`);
    }
    setHtml(detailEl, bits.join(''));
    submit.disabled = false;
    submit.dataset.tokens = q.tokensOut.toString();
    submit.dataset.wei = wei.toString();
    submit.dataset.graduates = String(q.graduates);

    // The contract's own quote is the authority; disagreement means our view of
    // the reserves is stale, so trust the chain and say so.
    try {
      const onChain = await quoteBuyOnChain(t.curve as Address, wei, wallet.address);
      if (onChain !== q.tokensOut) {
        submit.dataset.tokens = onChain.toString();
        setHtml(quoteEl, html`You receive<strong>${formatTokens(onChain)} ${t.symbol}</strong>`);
      }
    } catch {
      /* offline; the local mirror stands */
    }
  } else {
    let tokens: bigint;
    try {
      tokens = parseUnits(input.value.trim() || '0', 18);
    } catch {
      tokens = 0n;
    }
    if (tokens === 0n) {
      setHtml(quoteEl, html`<span>Enter an amount</span>`);
      setHtml(detailEl, '');
      submit.disabled = true;
      return;
    }
    if (tokens > balance) {
      setHtml(quoteEl, html`<span class="bad">More than your balance.</span>`);
      submit.disabled = true;
      return;
    }
    const q = quoteSell(state, tokens);
    if (q.quoteOut === 0n) {
      setHtml(quoteEl, html`<span>The curve cannot pay out that much.</span>`);
      submit.disabled = true;
      return;
    }
    setHtml(quoteEl, html`You receive<strong>${formatEth(q.quoteOut, {places: 6})} ETH</strong>`);
    setHtml(
      detailEl,
      html`<div class="qd"><span>Fee (1.3%)</span><span>${formatEth(q.fee, {places: 6})} ETH</span></div>
        <div class="qd"><span>Into this period's pool</span><span>${formatEth((q.fee * 50n) / 130n, {places: 6})} ETH</span></div>`,
    );
    submit.disabled = false;
    submit.dataset.out = q.quoteOut.toString();
    submit.dataset.tokensIn = tokens.toString();
    try {
      const onChain = await quoteSellOnChain(t.curve as Address, tokens);
      if (onChain !== q.quoteOut) {
        submit.dataset.out = onChain.toString();
        setHtml(quoteEl, html`You receive<strong>${formatEth(onChain, {places: 6})} ETH</strong>`);
      }
    } catch {
      /* offline */
    }
  }
}

export function setSide(next: 'buy' | 'sell'): void {
  side = next;
  render();
}

export function setSlippage(bps: bigint): void {
  slippageBps = bps;
  render();
}

export function applyPreset(value: string): void {
  const input = $<HTMLInputElement>('#trade-amount');
  if (!input || !current) return;
  if (value.endsWith('%')) {
    const pct = BigInt(parseInt(value, 10));
    input.value = formatUnits((current.balance * pct) / 100n, 18);
  } else {
    input.value = value;
  }
  void updateQuote();
}

export function applyMax(): void {
  const input = $<HTMLInputElement>('#trade-amount');
  if (!input || !current) return;
  input.value = formatUnits(current.balance, 18);
  void updateQuote();
}

/** Applies the slippage tolerance to whichever leg the user does not control. */
function withSlippage(amount: bigint): bigint {
  return (amount * (10_000n - slippageBps)) / 10_000n;
}

export async function submitTrade(): Promise<void> {
  if (!current) return;
  const t = current.token;
  const submit = $<HTMLButtonElement>('#trade-submit');
  if (!submit) return;
  if (!wallet.address) return notify('Connect a wallet first.', 'error');
  if (!onRightChain()) return notify('Switch your wallet to Robinhood Chain.', 'error');

  const p = txProgress(side === 'buy' ? `Buy ${t.symbol}` : `Sell ${t.symbol}`);
  try {
    if (side === 'buy') {
      const wei = BigInt(submit.dataset.wei ?? '0');
      const tokens = BigInt(submit.dataset.tokens ?? '0');
      await buy(
        {
          token: t.address as Address,
          curve: t.curve as Address,
          ethIn: wei,
          minTokensOut: withSlippage(tokens),
          graduates: submit.dataset.graduates === 'true',
        },
        (stage, detail, hash) => p.update(stage, detail, hash),
      );
    } else {
      const tokensIn = BigInt(submit.dataset.tokensIn ?? '0');
      const out = BigInt(submit.dataset.out ?? '0');
      await sell(
        {token: t.address as Address, curve: t.curve as Address, tokensIn, minQuoteOut: withSlippage(out)},
        (stage, detail, hash) => p.update(stage, detail, hash),
      );
    }
    closeModal();
    notify(side === 'buy' ? `Bought ${t.symbol}.` : `Sold ${t.symbol}.`, 'success');
    await load(t.address);
  } catch (e) {
    p.fail(describeError(e));
  }
}

export async function collectFees(token: string): Promise<void> {
  const p = txProgress('Collect pool fees');
  try {
    await collect(token as Address, (stage, detail, hash) => p.update(stage, detail, hash));
    closeModal();
    notify('Collected. The pool fees are in this period and credited to this token.', 'success');
    await load(token);
  } catch (e) {
    p.fail(describeError(e));
  }
}

export function currentToken(): TokenSummary | null {
  return current?.token ?? null;
}

export {spotPriceWei};
